
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * @component Button
 * @description Enhanced button component with various styles and premium animations
 * @props {boolean} asChild - Whether to render as a child component using Slot
 * @props {string} variant - Button style variant
 * @props {string} size - Button size
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:translate-y-[-1px] hover:brightness-105",
  {
    variants: {
      variant: {
        default: "bg-forti-fi text-white hover:bg-forti-fi-light shadow-sm hover:shadow-md",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm hover:shadow-md",
        outline:
          "border-2 border-forti-fi bg-transparent text-forti-fi hover:bg-forti-fi/10 shadow-sm hover:shadow-md",
        secondary:
          "bg-forti-fi-dark text-white hover:bg-forti-fi-dark/90 shadow-sm hover:shadow-md",
        ghost: "hover:bg-background hover:text-forti-fi",
        link: "text-forti-fi underline-offset-4 hover:underline p-0 h-auto",
        gradient: "bg-forti-fi text-white shadow-md hover:shadow-lg relative overflow-hidden",
        gold: "bg-gold text-white hover:bg-gold-light shadow-md hover:shadow-luxury-gold",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        xl: "h-12 rounded-md px-10 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

/**
 * ButtonProps interface extends React button props
 * and includes variants defined in buttonVariants
 */
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

/**
 * Button component with support for various styles and sizes
 * Can render as a button or as a child component (using Slot)
 * 
 * @param {ButtonProps} props - Button properties
 * @returns {JSX.Element} Styled button component
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    
    // Create content based on variant
    let content = children;
    
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {content}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
